"use client";

import { presentationColors as palette } from "@/shared/constants/presentation-colors";

import React, { useState, useEffect, useCallback } from "react";
import NavigationButton from "@/components/buttons/NavigationButton";
import { ISession } from "@/shared/models/session.model";
import Link from "next/link";
import SvgIcon from "@/components/SvgIcon";
import { getOrdinalSuffix } from "@/shared/utils/ordinal-suffix";
import { useAppSelector } from "@/lib/hooks";
import { useCurrency, CONVERTED_MARK } from "@/components/providers/CurrencyProvider";
import { UserRole } from "@/lib/types/auth";
import { getSessionLocationName } from "@/shared/utils/get-session-location-name";
import { DayOfWeekEnum } from "@/shared/enums/day-of-week.enum";
import { useForm } from "react-hook-form";
import RecurringSessionDatepicker from "@/components/ui-form/RecurringSessionDatepicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import FormattedSessionDateClient from "@/components/FormattedSessionDateClient";
import TimezoneHint from "@/components/TimezoneHint";
import { getSessionAvailabilityForDate } from "@/lib/api/sessions";
import { ISessionDateAvailability } from "@/shared/models/session.model";
import ProfessionalProfileSendMessageButton from "@/components/professional-profile/ProfessionalProfileSendMessageButton";
import { getSessionHosts } from "@/shared/utils/get-session-hosts";
import { getProfessionalAvailability, IProfessionalAvailability, IPausePeriod } from "@/lib/api/availability";
import { getPackagesForSession, IPackage, getMyCreditsBalance } from "@/lib/api/packages";
import { useSearchParams } from "next/navigation";
import { getProfileUrl } from "@/shared/utils/profile-url-utils";
import { getRemainingSessionSpaces, isGroupSession } from "@/shared/utils/group-session";
import { useSelectedOccurrence } from "@/lib/hooks/useSchedulingV2";
import { checkoutHrefV2, isV2SeriesWithoutDates, occurrenceAvailability, templateInstantsOn } from "@/shared/utils/scheduling-display";

const fp = (v: number) => v % 1 === 0 ? String(v) : v.toFixed(2);


// Helper function to check if a date falls within any pause period
const isDateInPausePeriod = (date: Date, pausePeriods: IPausePeriod[]): boolean => {
  if (!pausePeriods || pausePeriods.length === 0) return false;

  const checkDate = dayjs(date).startOf("day");

  return pausePeriods.some((period) => {
    const startDate = dayjs(period.startDate).startOf("day");
    const endDate = dayjs(period.endDate).startOf("day");

    return (
      checkDate.isSame(startDate) ||
      checkDate.isSame(endDate) ||
      (checkDate.isAfter(startDate) && checkDate.isBefore(endDate))
    );
  });
};

export default function SessionSidebar({ session, slug, showTotal = false }: { session: ISession; slug: string; showTotal?: boolean }) {
  // Extend dayjs with UTC plugin
  dayjs.extend(utc);
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);

  const searchParams = useSearchParams()

  // Check if this is a business session
  const isBusinessSession = !!session.isBusinessSession;

  // Check if user owns this session
  const isOwnSession = isAuthenticated && user?.id === session.professional?.id;

  // Show collaborate only for professional-to-professional
  const showCollaborate = isAuthenticated && user?.role === UserRole.PROFESSIONAL && !isOwnSession && !isBusinessSession;
  const [hideCollaborate, setHideCollaborate] = useState(false);
  const [bookingType, setBookingType] = useState("single");

  const groupSession = isGroupSession(session);
  const isPrivateSession = !groupSession && session.isPrivate === true;
  const { symbol: cs, convert: cv, currency: displayCurrency } = useCurrency();
  const sessionCurrency = session.currency || "GBP";
  // Same rule as <Currency>: converted amounts carry the "≈" mark.
  const cp = (amount: number) => `${displayCurrency === sessionCurrency ? "" : CONVERTED_MARK}${cs}${fp(cv(amount, sessionCurrency))}`;

  const [sessionPackages, setSessionPackages] = useState<IPackage[]>([]);
  const [walletCredits, setWalletCredits] = useState<number | null>(null);

  // Bundle controls only exist when every required term is present in real session data.
  const shouldShowBundle =
    session.isBundleEnabled === true &&
    typeof session.bundleMinSessions === "number" &&
    session.bundleMinSessions > 0 &&
    typeof session.bundleDiscountPercent === "number" &&
    session.bundleDiscountPercent > 0 &&
    session.bundleDiscountPercent < 100;
  const isLegacyPackageOnly = session.isPackageOnly === true;
  const hasPackageOnlyPackage = sessionPackages.some((pkg) => pkg.isPackageOnly);
  const isPackageOnly = isLegacyPackageOnly || hasPackageOnlyPackage;

  const minBundleSessions = shouldShowBundle ? session.bundleMinSessions! : 0;
  const bundleDiscountPercent = shouldShowBundle ? session.bundleDiscountPercent! : 0;
  const bundleTicketCount = minBundleSessions;
  const [isPastSession, setIsPastSession] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [sessionDateAvailability, setSessionDateAvailability] = useState<ISessionDateAvailability | null>(null);
  const [isFetchingAvailability, setIsFetchingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [professionalAvailability, setProfessionalAvailability] = useState<IProfessionalAvailability | null>(null);
  const [isFetchingProfessionalAvailability, setIsFetchingProfessionalAvailability] = useState(false);
  const [professionalAvailabilityAttempted, setProfessionalAvailabilityAttempted] = useState(false);
  // v2: the chosen day as its server occurrence (instants, capacity, revision).
  const { schedulingV2, selectedOccurrence, setSelectedOccurrence } = useSelectedOccurrence();
  // v2 capacity is the chosen occurrence's (derived here, not copied into state).
  const dateAvailability = schedulingV2 ? (selectedOccurrence ? occurrenceAvailability(selectedOccurrence) : null) : sessionDateAvailability;

  // Setup form for recurring session date selection
  const { control, setValue } = useForm({
    defaultValues: {
      sessionDate: null as Date | null
    }
  });

  useEffect(() => {
    if (isPackageOnly && shouldShowBundle) {
      setBookingType("bundle");
    } else if (!shouldShowBundle && bookingType === "bundle") {
      setBookingType("single");
    }
  }, [shouldShowBundle, bookingType, isPackageOnly]);

  useEffect(() => {
    if (session.id && !isBusinessSession) {
      getPackagesForSession(session.id)
        .then((packages) => setSessionPackages(packages.filter((pkg) => pkg.isActive)))
        .catch(() => {});
    }
    // Wallet credits live behind a JwtGuard (`GET /credits/balance`). Only
    // fetch them for logged-in users — calling it logged-out returns 401,
    // which the httpClient interceptor turns into a forced logout + redirect
    // to /login, so a guest just opening a session page got bounced to login.
    if (isAuthenticated) {
      getMyCreditsBalance()
        .then((d) => setWalletCredits(d.walletCredits))
        .catch(() => {});
    }
  }, [session.id, isBusinessSession, isAuthenticated]);

  useEffect(() => {
    if (!selectedDate) {
      setSessionDateAvailability(null);
      setAvailabilityError(null);
    }
  }, [selectedDate, session]);

  useEffect(() => {
    const checkSessionTime = () => {
      const now = dayjs(); // Current time in user's timezone

      if (session.isRecurring && selectedOccurrence) {
        // v2: the occurrence's own end instant.
        setIsPastSession(now.isAfter(dayjs(selectedOccurrence.endAt)));
      } else if (session.isRecurring && selectedDate) {
        const selectedDateStr = dayjs(selectedDate).format("YYYY-MM-DD");
        // Convert UTC endAt to local time for comparison
        const sessionEndUTC = dayjs.utc(session.endAt);
        const selectedSessionEndTime = dayjs
          .utc(`${selectedDateStr} ${sessionEndUTC.format("HH:mm")}`, "YYYY-MM-DD HH:mm")
          .local();

        const isPast = now.isAfter(selectedSessionEndTime);
        setIsPastSession(isPast);
      } else if (session.isRecurring && !selectedDate) {
        setIsPastSession(false);
      } else {
        // Convert UTC endAt to local time for comparison
        const sessionEndLocal = dayjs.utc(session.endAt).local();
        const isPast = now.isAfter(sessionEndLocal);
        setIsPastSession(isPast);
      }
    };

    checkSessionTime();

    const interval = setInterval(checkSessionTime, 60000);
    return () => clearInterval(interval);
  }, [session, selectedDate, selectedOccurrence]);

  // Get the host (professional field contains business data for business sessions)
  const host = session?.professional;
  const hostUrl = getProfileUrl(host);
  const hostName = host?.nameOfBusiness || host?.firstName;

  // Keep professional variable for backward compatibility with existing code
  const professional = session?.professional;

  // Fetch professional availability to get pause periods (only for professional sessions)
  useEffect(() => {
    const fetchProfessionalAvailability = async () => {
      if (!professional?.id || session?.isBusinessSession) return; // Skip for business sessions

      setIsFetchingProfessionalAvailability(true);
      try {
        const availability = await getProfessionalAvailability(professional.id);
        setProfessionalAvailability(availability);
      } catch (error) {
        console.error("Error fetching professional availability:", error);
        // Don't set error state for pause periods - just log it
        // Booking should still work even if pause periods can't be fetched
      } finally {
        setIsFetchingProfessionalAvailability(false);
        setProfessionalAvailabilityAttempted(true);
      }
    };

    fetchProfessionalAvailability();
  }, [professional?.id, session?.isBusinessSession]);

  // Get all hosts for collaborative sessions
  const sessionHosts = getSessionHosts(session);
  const isCollaborativeSession = sessionHosts.length > 1;
  const primaryFocusArea = session.focusAreas?.[0]?.focusArea?.name;

  const {
    id,
    title,
    startAt,
    endAt,
    locationType,
    availableSlots: rawAvailableSlots,
    city,
    isRecurring,
    recurringDays = [],
    price: sessionPrice
  } = session;

  const sessionStartAtDate = new Date(session.startAt);
  const sessionEndAtDate = new Date(session.endAt);

  // v2: the chosen (else next) occurrence's instants; a series with none shows no time.
  // v1 with a chosen date: the template clock on that date (what gets booked), not the template date's offset.
  const v1OnDate = !schedulingV2 && isRecurring ? templateInstantsOn(session, selectedDate) : null;
  const timeSource: { startAt?: string; endAt?: string } =
    selectedOccurrence ?? v1OnDate ?? (isV2SeriesWithoutDates(session) ? {} : session.nextOccurrence ?? session);
  const formattedDate = timeSource.startAt ? dayjs.utc(timeSource.startAt).local().format("dddd D MMM") : "";
  const startTime = timeSource.startAt ? dayjs.utc(timeSource.startAt).local().format("HH:mm") : "";
  const endTime = timeSource.endAt
    ? (() => {
        const endDate = dayjs.utc(timeSource.endAt).local();
        const isEndOfDay = endDate.hour() === 23 && endDate.minute() === 59 && endDate.second() === 59;
        return isEndOfDay ? "00:00" : endDate.format("HH:mm");
      })()
    : "";
  const displayDateTime = `${formattedDate}, ${startTime} - ${endTime}`;
  const displayTime = startTime && endTime ? `${startTime} - ${endTime}` : "";

  const locationString = getSessionLocationName(session);

  // Calculate available slots (handle negative values)
  const currentAvailableSlots = isRecurring && selectedDate
    ? dateAvailability
      ? getRemainingSessionSpaces({
          slots: dateAvailability.totalSlots,
          availableSlots: dateAvailability.availableSlots
        })
      : null
    : getRemainingSessionSpaces({
        slots: session.slots,
        availableSlots: rawAvailableSlots
      });
  // Sold-out logic should NOT apply to private sessions
  const isSoldOut = isPrivateSession
    ? false
    : selectedDate && dateAvailability
    ? dateAvailability.isSoldOut
    : currentAvailableSlots === 0;

  // Set ticket counts
  const singleTicketCount = 1;

  // Set prices
  // ?? not || — price 0 is a real (free) price, not missing data.
  const singleSessionPrice = sessionPrice ?? 0;
  const bundleDiscountMultiplier = (100 - bundleDiscountPercent) / 100;
  const bundleSessionPrice = Math.round(singleSessionPrice * bundleDiscountMultiplier);
  const totalBundlePrice = bundleSessionPrice * bundleTicketCount;

  const handleBookingTypeChange = (type) => {
    setBookingType(type);
  };

  // Handle date selection for recurring sessions
  const handleDateChange = useCallback(
    async (date: Date | null) => {
      setSelectedDate(date);
      setValue("sessionDate", date);
      // v2: capacity comes from the chosen occurrence (onOccurrenceChange), no per-date request.
      if (schedulingV2) return;
      setSessionDateAvailability(null);
      setAvailabilityError(null);

      if (date && session && session.isRecurring) {
        setIsFetchingAvailability(true);
        try {
          const formattedApiDate = dayjs(date).format("MM-DD-YYYY");
          const availability = await getSessionAvailabilityForDate(session.id, formattedApiDate);
          setSessionDateAvailability(availability);
        } catch (error: any) {
          console.error("Error fetching session availability:", error);
          setAvailabilityError(error.message || "Failed to fetch availability. Please try another date.");
          setSessionDateAvailability(null);
        }
        setIsFetchingAvailability(false);
      }
    },
    [session, setValue, schedulingV2]
  );

  // Check if the session date (single or selected recurring date) is in a pause period
  const pausePeriods = professionalAvailability?.pausePeriods || [];
  // Only consider pause periods ready when we've attempted to fetch them and finished loading
  const pausePeriodsReady = !isFetchingProfessionalAvailability && professionalAvailabilityAttempted;
  // v2: no client pause check (professional-local dates); occurrence availability includes pauses.
  const isSessionDatePaused = schedulingV2
    ? false
    : isRecurring
    ? !!(selectedDate && isDateInPausePeriod(selectedDate, pausePeriods))
    : isDateInPausePeriod(sessionStartAtDate, pausePeriods);

  // Single session booking validation (complex)
  const isBookingDisabled =
    !pausePeriodsReady || // Keep disabled until pause periods are loaded
    isPastSession ||
    (!isPrivateSession && isSoldOut) ||
    isOwnSession ||
    (isRecurring && !selectedDate) ||
    (isRecurring && selectedDate && !dateAvailability && !isFetchingAvailability && !availabilityError) || // Date selected, no data yet (and not loading/error)
    (isRecurring && selectedDate && !!availabilityError) || // Date selected but there was an error fetching - ensure boolean check
    isFetchingAvailability || // Still fetching availability
    isSessionDatePaused; // Session date is within a paused period

  let bookingButtonLabel = "Book Now";
  if (!pausePeriodsReady) {
    bookingButtonLabel = "Loading...";
  } else if (isOwnSession) {
    bookingButtonLabel = "Cannot Book Own Session";
  } else if (isPastSession) {
    bookingButtonLabel = "Session Ended";
  } else if (isSessionDatePaused) {
    bookingButtonLabel = "Professional Unavailable";
  } else if (isFetchingAvailability) {
    bookingButtonLabel = "Checking Availability...";
  } else if (!isPrivateSession && isSoldOut) {
    bookingButtonLabel = "Sold Out";
  } else if (isRecurring && !selectedDate) {
    bookingButtonLabel = "Select a date";
  } else if (isRecurring && selectedDate && availabilityError) {
    bookingButtonLabel = "Availability Error";
  } else if (isRecurring && selectedDate && !dateAvailability && !isFetchingAvailability) {
  }

  // v2: the chosen (else next) occurrence; v1 with a chosen date: the template clock on that date.
  const v2Shown = schedulingV2 ? selectedOccurrence ?? session.nextOccurrence ?? null : null;
  const shown = v2Shown ?? v1OnDate;

  // Generate booking URL with selected date for sessions
  const getBookingUrl = () => {
    // v2: the chosen occurrence (occurrenceDate + revision).
    if (isRecurring && selectedOccurrence) {
      return checkoutHrefV2(session, slug, selectedOccurrence, searchParams?.get("recommId"));
    }
    const bookingParams = new URLSearchParams();
    const recommId = searchParams?.get("recommId");
    if (recommId) bookingParams.set("recommId", recommId);

    // Add selected date as query parameter for recurring sessions
    if (isRecurring && selectedDate) {
      const formattedDate = dayjs(selectedDate).format("MM-DD-YYYY");
      bookingParams.set("sessionDate", formattedDate);
    } else if (!isRecurring) {
      // Add session date for non-recurring sessions as well
      const formattedDate = dayjs.utc(session.startAt).local().format("MM-DD-YYYY");
      bookingParams.set("sessionDate", formattedDate);
    }

    const query = bookingParams.toString();
    return `/book/session/${slug}${query ? `?${query}` : ""}`;
  };

  const getAvailabilityUrl = () => {
    const bookingParams = new URLSearchParams();
    const recommId = searchParams?.get("recommId");
    if (recommId) bookingParams.set("recommId", recommId);
    const query = bookingParams.toString();
    return `/session/${slug}/availability${query ? `?${query}` : ""}`;
  };

  const getBundleBookingUrl = (tickets: number) => {
    const bookingParams = new URLSearchParams({ tickets: String(tickets), bundle: "true" });
    const recommId = searchParams?.get("recommId");
    if (recommId) bookingParams.set("recommId", recommId);
    return `/book/session/${slug}?${bookingParams.toString()}`;
  };

  if (showCollaborate && !hideCollaborate) {
    return (
      <div className="sidebar-collaborate">
        <div>
          <h3>Ready to collaborate?</h3>
          <p>
            {isCollaborativeSession
              ? `This collaborative session includes multiple professionals you're interested in collaborating with. Would you like to connect and explore potential opportunities together?`
              : `${host?.firstName || "This professional"} offers${primaryFocusArea ? ` ${primaryFocusArea}` : ""} sessions. Would you like to connect and explore potential opportunities together?`}
          </p>
        </div>
        {sessionHosts.map((host) => (
          <ProfessionalProfileSendMessageButton
            key={host.id}
            className="btn__bg--dark-grey sidebar-collaborate__message-button"
            user={host}
            text={`Yes, message ${host.firstName}`}
          />
        ))}
        <NavigationButton className="btn__bg--light-grey" onClick={() => setHideCollaborate(true)}>
          Not right now
        </NavigationButton>
      </div>
    );
  }

  // If this is the professional's or business's own session, show "Add your services to your profile" section instead of booking form
  if (isAuthenticated && isOwnSession && (user?.role === UserRole.PROFESSIONAL || user?.role === UserRole.BUSINESS)) {
    const accountType = user?.role === UserRole.BUSINESS ? "business" : "professional";
    return (
      <div className="professional-profile-page__add-sessions">
        <h3>Add your services to your profile</h3>
        <p>Add your available services to your profile so clients can book in with ease.</p>

        <NavigationButton href={`/${accountType}-account/services?createService=true`} className="btn__bg--dark-grey">
          Create service
        </NavigationButton>
      </div>
    );
  }

  // If this is the professional's own session, show warning in booking form
  if (isAuthenticated && isOwnSession) {
    // Continue to show booking form, but with disabled button
  }

  // For private sessions, render simplified sidebar
  if (isPrivateSession) {
    return (
      <div className="sidebar-booking">
        {!isPackageOnly && (
        <div
          className={`sidebar-booking-option ${bookingType === "single" ? "sidebar-booking-option--active" : ""} ${
            !shouldShowBundle ? "sidebar-booking-option--solo" : ""
          }`}
        >
          <button
            type="button"
            className="sidebar-booking-header"
            onClick={() => handleBookingTypeChange("single")}
            aria-label="Select single session booking"
          >
            <div className="sidebar-booking-hleft">
              <h2 className="sidebar-booking-title">Book this session</h2>
              <p className="sidebar-booking-price">
                <span>{cp(session.price)}</span> per session
              </p>
              {walletCredits !== null && walletCredits >= session.price && session.price > 0 && (
                <p style={{ fontSize: "11px", color: palette.accent, marginTop: "2px", fontWeight: 500 }}>
                  Pay with credits ({cp(walletCredits)} available)
                </p>
              )}
            </div>
            {/* Only show radio button when bundle option is visible */}
            {shouldShowBundle && (
              <div
                className={`sidebar-booking-radio ${bookingType === "single" ? "sidebar-booking-radio--active" : ""}`}
              >
                <div className="sidebar-booking-radio-dot"></div>
              </div>
            )}
          </button>

          <div
            className={`sidebar-booking-details ${
              bookingType === "single" ? "sidebar-booking-details--visible" : "sidebar-booking-details--hidden"
            }`}
          >
            {/* Duration info */}
            {(isBusinessSession ? (session.customDuration || session.duration) : session.duration > 0) && (
              <div className="sidebar-booking-time">
                <div className="sidebar-booking-time-text">
                  {isBusinessSession
                    ? (session.customDuration || (session.duration ? `${session.duration} minutes` : ""))
                    : `${session.duration} minutes`}
                </div>
                <div className="sidebar-booking-time-icon">
                  <SvgIcon icon="clock" iconType="feather" />
                </div>
              </div>
            )}

            {/* Location info */}
            <div className="sidebar-booking-location">
              <div className="sidebar-booking-location-text">{getSessionLocationName(session)}</div>
              <div className="sidebar-booking-location-icon">
                {session.locationType === "VIRTUAL" ? (
                  <SvgIcon icon="video" iconType="feather" />
                ) : (
                  <SvgIcon icon="map-pin" iconType="feather" />
                )}
              </div>
            </div>

            {isOwnSession && (
              <div className="sidebar-booking-own-profile">
                <p className="sidebar-booking-own-profile-text">
                  This is your own session. You cannot book sessions you're hosting.
                </p>
              </div>
            )}

            <div className="sidebar-booking-actions">
              {showTotal && <div className="sidebar-booking-total"><span>Total:</span><span>{cp(session.price)}</span></div>}
              {isOwnSession ? (
                <NavigationButton className="btn__bg--dark-grey button-disabled" disabled>
                  See available slots
                </NavigationButton>
              ) : (
                <NavigationButton href={getAvailabilityUrl()} className="btn__bg--dark-grey">
                  See available slots
                </NavigationButton>
              )}
            </div>

            <div className="sidebar-booking-disclaimer-container">
              <p className="sidebar-booking-disclaimer">
                You can cancel or reschedule your session anytime before, 48 hours prior to your session. See our{" "}
                <Link href="/terms-and-conditions" target="_blank" aria-label="View Terms and Conditions">
                  Terms and Conditions
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
        )}

        {/* Bundle option for private sessions if enabled */}
        {shouldShowBundle && !isOwnSession && (
          <div className={`sidebar-booking-option ${bookingType === "bundle" ? "sidebar-booking-option--active" : ""}`}>
            {isPackageOnly && (
              <p style={{ fontSize: "13px", color: "#8B6F4E", background: "#FDF6ED", padding: "8px 12px", borderRadius: "8px", marginBottom: "8px" }}>
                This service is available as a package only
              </p>
            )}
            <button
              type="button"
              className="sidebar-booking-header"
              onClick={() => handleBookingTypeChange("bundle")}
              aria-label="Select bundle booking"
            >
              <div className="sidebar-booking-hleft">
                <h2 className="sidebar-booking-title">Purchase a bundle</h2>
                <p className="sidebar-booking-bundle-summary">
                  {minBundleSessions} Sessions for {cp(
                    session.price *
                      minBundleSessions *
                      ((100 - bundleDiscountPercent) / 100)
                  )}
                </p>
                <p className="sidebar-booking-price sidebar-booking-price--discount">
                  {cp(session.price * ((100 - bundleDiscountPercent) / 100))} per session
                </p>
              </div>
              <div
                className={`sidebar-booking-radio ${bookingType === "bundle" ? "sidebar-booking-radio--active" : ""}`}
              >
                <div className="sidebar-booking-radio-dot"></div>
              </div>
            </button>

            <div
              className={`sidebar-booking-details ${
                bookingType === "bundle" ? "sidebar-booking-details--visible" : "sidebar-booking-details--hidden"
              }`}
            >
              <div className="sidebar-booking-actions">
              {showTotal && <div className="sidebar-booking-total"><span>Total:</span><span>{cp(session.price * minBundleSessions * ((100 - bundleDiscountPercent) / 100))}</span></div>}
                <NavigationButton
                  href={getBundleBookingUrl(minBundleSessions)}
                  className="sidebar-booking-purchase btn__bg--dark-grey"
                >
                  Buy now
                </NavigationButton>
              </div>

              <div className="sidebar-booking-disclaimer-container">
                <p className="sidebar-booking-disclaimer">
                  You can cancel or reschedule your session anytime before, 48 hours prior to your session. See our{" "}
                  <Link href="/terms-and-conditions" target="_blank" aria-label="View Terms and Conditions">
                    Terms and Conditions
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Package options */}
        {sessionPackages.length > 0 && !isOwnSession && (
          <div style={{ marginTop: "16px" }}>
            {!isPackageOnly && (
              <p style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px", color: "#9ca3af", marginBottom: "12px", textAlign: "center" }}>
                — or —
              </p>
            )}
            {isPackageOnly && (
              <p style={{ fontSize: "13px", color: "#8B6F4E", background: "#FDF6ED", padding: "8px 12px", borderRadius: "8px", marginBottom: "12px" }}>
                This session is available as a package only
              </p>
            )}
            <h3 style={{ fontSize: "15px", fontWeight: 600, marginBottom: "12px" }}>Packages available</h3>
            {sessionPackages.map((pkg) => (
              <div key={pkg.id} style={{ border: "1px solid #e5e7eb", borderRadius: "12px", padding: "14px", marginBottom: "10px", background: "#fafafa" }}>
                <p style={{ fontWeight: 600, fontSize: "14px", marginBottom: "4px" }}>{pkg.title}</p>
                <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "4px" }}>
                  {pkg.quantity} sessions · {cp(pkg.totalPrice)}
                </p>
                <p style={{ fontSize: "12px", color: palette.accent, marginBottom: "4px" }}>
                  {cp(pkg.totalPrice / pkg.quantity)}/session
                  {pkg.validityWeeks ? ` · Use within ${pkg.validityWeeks} weeks` : ""}
                </p>
                <NavigationButton
                  className="btn__bg--dark-grey"
                  href={`/bundle/${pkg.id}`}
                  style={{ width: "100%", marginTop: "8px", fontSize: "13px", padding: "8px" }}
                >
                  View package
                </NavigationButton>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="sidebar-booking">
      {/* Single Booking Option */}
      {!isPackageOnly && (
      <div
        className={`sidebar-booking-option ${bookingType === "single" ? "sidebar-booking-option--active" : ""} ${
          !shouldShowBundle ? "sidebar-booking-option--solo" : ""
        }`}
      >
        <button
          type="button"
          className="sidebar-booking-header"
          onClick={() => handleBookingTypeChange("single")}
          aria-label="Select single session booking"
        >
          <div className="sidebar-booking-hleft">
            <h2 className="sidebar-booking-title">Book your place</h2>
            <p className="sidebar-booking-price">
              <span>{cp(singleSessionPrice)}</span> per session
            </p>
            {walletCredits !== null && walletCredits >= singleSessionPrice && singleSessionPrice > 0 && (
              <p style={{ fontSize: "11px", color: palette.accent, marginTop: "2px", fontWeight: 500 }}>
                Pay with credits ({cp(walletCredits)} available)
              </p>
            )}
          </div>
          {/* Only show radio button when bundle option is visible */}
          {shouldShowBundle && (
            <div className={`sidebar-booking-radio ${bookingType === "single" ? "sidebar-booking-radio--active" : ""}`}>
              <div className="sidebar-booking-radio-dot"></div>
            </div>
          )}
        </button>

        <div
          className={`sidebar-booking-details ${
            bookingType === "single" ? "sidebar-booking-details--visible" : "sidebar-booking-details--hidden"
          }`}
        >
          {isRecurring ? (
            <>
              <div className="sidebar-booking-recurring-date">
                <div className="sidebar-booking-date-selection">
                  <RecurringSessionDatepicker
                    name="sessionDate"
                    control={control as any}
                    label="Select date"
                    startDate={session.startAt}
                    recurringDays={recurringDays}
                    maxWeeks={52}
                    onChange={handleDateChange}
                    required
                    autoSelectFirstAvailable={true}
                    sessionId={session.id}
                    onOccurrenceChange={setSelectedOccurrence}
                    sessionInfo={{
                      // v2: the chosen (else next) occurrence's own instants, viewer-local.
                      startTime: shown
                        ? dayjs(shown.startAt).format("HH:mm")
                        : isV2SeriesWithoutDates(session)
                        ? undefined
                        : session.startAt
                        ? dayjs.utc(session.startAt).local().format("HH:mm")
                        : undefined,
                      endTime: shown
                        ? dayjs(shown.endAt).format("HH:mm")
                        : isV2SeriesWithoutDates(session)
                        ? undefined
                        : session.endAt
                        ? (() => {
                            const endDate = dayjs.utc(session.endAt).local();
                            const isEndOfDay =
                              endDate.hour() === 23 && endDate.minute() === 59 && endDate.second() === 59 && true;
                            return isEndOfDay ? "00:00" : endDate.format("HH:mm");
                          })()
                        : undefined,
                      location: getSessionLocationName(session),
                      price: session.price
                    }}
                    availabilityInfo={{
                      availableSlots: dateAvailability?.availableSlots,
                      isLoading: isFetchingAvailability,
                      error: availabilityError || undefined
                    }}
                    pausePeriods={pausePeriods}
                    pausePeriodsLoaded={pausePeriodsReady}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="sidebar-booking-single-date">
              <div className="sidebar-booking-single-date-text">
                <FormattedSessionDateClient session={session} showTime={false} />
              </div>
              <div className="sidebar-booking-single-date-icon">
                <SvgIcon icon="calendar" iconType="feather" />
              </div>
            </div>
          )}

          {/* Don't show time for business sessions */}
          {!isBusinessSession && (
            <div className="sidebar-booking-time">
              <div className="sidebar-booking-time-text">
                {displayTime}
                <TimezoneHint />
              </div>
              <div className="sidebar-booking-time-icon">
                <SvgIcon icon="clock" iconType="feather" />
              </div>
            </div>
          )}

          {/* Location display - Don't show for business sessions */}
          {!isBusinessSession && (
            <div className="sidebar-booking-location">
              <div className="sidebar-booking-location-text">{locationString}</div>
              <div className="sidebar-booking-location-icon">
                {locationType === "VIRTUAL" ? (
                  <SvgIcon icon="video" iconType="feather" />
                ) : (
                  <SvgIcon icon="map-pin" iconType="feather" />
                )}
              </div>
            </div>
          )}

          {/* Ticket display */}
          <div className="sidebar-booking-tickets">
            <div className="sidebar-booking-tickets-info">
              <div className="sidebar-booking-tickets-label">Tickets</div>
              {(!isRecurring || selectedDate) && (
                <div
                  className={`sidebar-booking-tickets-available ${
                    isBookingDisabled || isSoldOut ? "sidebar-booking-tickets-available--sold-out" : ""
                  }`}
                >
                  {isFetchingAvailability
                    ? "Loading..."
                    : currentAvailableSlots === null
                    ? "Availability unavailable"
                    : `${currentAvailableSlots} ${currentAvailableSlots === 1 ? "space" : "spaces"} remaining`}
                </div>
              )}
            </div>
            <div className="sidebar-booking-tickets-display">
              {isRecurring && !selectedDate ? "Select a date" : isSoldOut ? "Sold out" : singleTicketCount}
            </div>
          </div>

          {/* Display appropriate message */}
          {availabilityError && isRecurring && selectedDate && (
            <div className="sidebar-booking-error">
              <p className="sidebar-booking-error-text">{availabilityError}</p>
            </div>
          )}

          {isOwnSession && (
            <div className="sidebar-booking-own-profile">
              <p className="sidebar-booking-own-profile-text">
                This is your own session. You cannot book sessions you're hosting.
              </p>
            </div>
          )}

          {isPastSession && (
            <div className="sidebar-booking-past">
              <p className="sidebar-booking-past-text">
                {isRecurring && selectedDate
                  ? `This session for ${dayjs(selectedDate).format("dddd, MMMM D")} has already ended.`
                  : "This session has already ended."}
                {hostUrl && (
                  <>
                    {" "}Please check other <Link href={hostUrl}>upcoming sessions{hostName ? ` from ${hostName}` : ""}</Link>.
                  </>
                )}
              </p>
            </div>
          )}

          {isSessionDatePaused && !isPastSession && (
            <div className="sidebar-booking-paused">
              <p className="sidebar-booking-paused-text">
                {isRecurring && selectedDate
                  ? `${hostName || "The professional"} is unavailable on ${dayjs(selectedDate).format("dddd, MMMM D")}.`
                  : `${host?.firstName || "The professional"} is unavailable for this session.`}
                {hostUrl && (
                  <>
                    {" "}Please check other <Link href={hostUrl}>available sessions{hostName ? ` from ${hostName}` : ""}</Link>.
                  </>
                )}
              </p>
            </div>
          )}

          {(!isRecurring || selectedDate) &&
            !isPrivateSession &&
            !isPastSession &&
            isSoldOut &&
            !isFetchingAvailability && (
              <div className="sidebar-booking-sold-out">
                <p className="sidebar-booking-sold-out-text">
                  This session is fully booked.
                  {hostUrl && (
                    <>
                      {" "}Please check other <Link href={hostUrl}>available sessions{hostName ? ` from ${hostName}` : ""}</Link>.
                    </>
                  )}
                </p>
              </div>
            )}

          <div className="sidebar-booking-actions">
              {showTotal && <div className="sidebar-booking-total"><span>Total:</span><span>{cp(singleSessionPrice)}</span></div>}
            {isBookingDisabled ? (
              <NavigationButton className="btn__bg--brand-orange button-disabled" disabled>
                {bookingButtonLabel}
              </NavigationButton>
            ) : (
              <NavigationButton href={getBookingUrl()} className="btn__bg--brand-orange">
                {bookingButtonLabel}
              </NavigationButton>
            )}
          </div>

          {/* Single booking disclaimer */}
          <div className="sidebar-booking-disclaimer-container">
            <p className="sidebar-booking-disclaimer">
              You can cancel or reschedule your session anytime before, 48 hours prior to your session. See our{" "}
              <Link href="/terms-and-conditions" target="_blank" aria-label="View Terms and Conditions">
                Terms and Conditions
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
      )}

      {shouldShowBundle && !isOwnSession && (
        <div className={`sidebar-booking-option ${bookingType === "bundle" ? "sidebar-booking-option--active" : ""}`}>
          {isPackageOnly && (
            <p style={{ fontSize: "13px", color: "#8B6F4E", background: "#FDF6ED", padding: "8px 12px", borderRadius: "8px", marginBottom: "8px" }}>
              This service is available as a package only
            </p>
          )}
          <button
            type="button"
            className="sidebar-booking-header"
            onClick={() => handleBookingTypeChange("bundle")}
            aria-label="Select bundle booking"
          >
            <div className="sidebar-booking-hleft">
              <h2 className="sidebar-booking-title">Purchase a bundle</h2>
              <p className="sidebar-booking-bundle-summary">
                {bundleTicketCount} Sessions for {cp(totalBundlePrice)}
              </p>
              <p className="sidebar-booking-price sidebar-booking-price--discount">{cp(bundleSessionPrice)} per session</p>
            </div>
            <div className={`sidebar-booking-radio ${bookingType === "bundle" ? "sidebar-booking-radio--active" : ""}`}>
              <div className="sidebar-booking-radio-dot"></div>
            </div>
          </button>

          <div
            className={`sidebar-booking-details ${
              bookingType === "bundle" ? "sidebar-booking-details--visible" : "sidebar-booking-details--hidden"
            }`}
          >
            <div className="sidebar-booking-actions">
              {showTotal && <div className="sidebar-booking-total"><span>Total:</span><span>{cp(totalBundlePrice)}</span></div>}
              <NavigationButton
                href={getBundleBookingUrl(bundleTicketCount)}
                className="sidebar-booking-purchase btn__bg--dark-grey"
              >
                Buy now
              </NavigationButton>
            </div>

            {/* Bundle booking disclaimer */}
            <div className="sidebar-booking-disclaimer-container">
              <p className="sidebar-booking-disclaimer">
                You can cancel or reschedule your session anytime before, 48 hours prior to your session. See our{" "}
                <Link href="/terms-and-conditions" target="_blank" aria-label="View Terms and Conditions">
                  Terms and Conditions
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Package options for group sessions */}
      {sessionPackages.length > 0 && !isOwnSession && (
        <div style={{ marginTop: "16px" }}>
          {!isPackageOnly && (
            <p style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px", color: "#9ca3af", marginBottom: "12px", textAlign: "center" }}>
              — or —
            </p>
          )}
          {isPackageOnly && (
            <p style={{ fontSize: "13px", color: "#8B6F4E", background: "#FDF6ED", padding: "8px 12px", borderRadius: "8px", marginBottom: "12px" }}>
              This session is available as a package only
            </p>
          )}
          <h3 style={{ fontSize: "15px", fontWeight: 600, marginBottom: "12px" }}>Packages available</h3>
          {sessionPackages.map((pkg) => (
            <div key={pkg.id} style={{ border: "1px solid #e5e7eb", borderRadius: "12px", padding: "14px", marginBottom: "10px", background: "#fafafa" }}>
              <p style={{ fontWeight: 600, fontSize: "14px", marginBottom: "4px" }}>{pkg.title}</p>
              <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "4px" }}>
                {pkg.quantity} sessions · {cp(pkg.totalPrice)}
              </p>
              <p style={{ fontSize: "12px", color: palette.accent, marginBottom: "4px" }}>
                {cp(pkg.totalPrice / pkg.quantity)}/session
                {pkg.validityWeeks ? ` · Use within ${pkg.validityWeeks} weeks` : ""}
              </p>
              <NavigationButton
                className="btn__bg--dark-grey"
                href={`/bundle/${pkg.id}`}
                style={{ width: "100%", marginTop: "8px", fontSize: "13px", padding: "8px" }}
              >
                View package
              </NavigationButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
